import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Grid,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Link,
  Tab,
  Tabs,
  SelectChangeEvent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CancelIcon from "@mui/icons-material/Cancel";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import { callApi } from "../../../utils/apiHelper";
import { apiEndpoints } from "../../../utils/apiEndpoints";
import {
  PurchaseOrder as BasePurchaseOrder,
  RawMaterial,
  UnitType,
  UnitConversion,
  getCompanyUsers,
  getRawMaterials,
  User,
} from "../../../utils/commonApis";
import UnitConverter from "../../UnitConverter";

// Extended PurchaseOrder interface with additional fields from the Lambda
interface PurchaseOrder extends BasePurchaseOrder {
  Comments?: string;
  ExpectedDeliveryDate?: number;
  EntryExitId?: string;
  CancellationReason?: string;
  VendorPhone?: string;
}

// Status color mapping
const statusColors: { [key: string]: string } = {
  Pending: "#FFA500", // Orange
  Approved: "#4CAF50", // Green
  Rejected: "#F44336", // Red
  Cancelled: "#9E9E9E", // Grey
  Completed: "#2196F3", // Blue
};

const statusOptions = [
  { value: "Pending", label: "Pending" },
  { value: "Approved", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "Completed", label: "Completed" },
];

interface POFormData {
  VendorId: string;
  Comments: string;
  ExpectedDeliveryDate: number;
  RawMaterials: POFormMaterial[];
  EntryExitId?: string;
  Status?: string;
}

interface POFormMaterial {
  RawMaterialId: string;
  Quantity: number;
  Price: number;
  BaseUnit: string;
  UnitConversions?: UnitConversion[];
}

// Extended RawMaterial interface for Purchase Orders
interface PORawMaterial extends RawMaterial {
  Quantity: number;
  Price: number;
}

// Response type for purchase order API
interface PurchaseOrderResponse {
  PurchaseOrders: PurchaseOrder[];
  Count: number;
}

const PurchaseOrderManagement: React.FC = () => {
  // State for purchase orders
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // State for form
  const [openForm, setOpenForm] = useState<boolean>(false);
  const [formMode, setFormMode] = useState<"create" | "edit" | "cancel">(
    "create",
  );
  const [formData, setFormData] = useState<POFormData>({
    VendorId: "",
    Comments: "",
    ExpectedDeliveryDate: Date.now() + 7 * 24 * 60 * 60 * 1000, // 7 days from now
    RawMaterials: [],
  });
  const [selectedPO, setSelectedPO] = useState<PurchaseOrder | null>(null);
  const [cancellationReason, setCancellationReason] = useState<string>("");

  // State for vendors and raw materials
  const [vendors, setVendors] = useState<User[]>([]);
  const [rawMaterialsData, setRawMaterialsData] = useState<RawMaterial[]>([]);

  // State for material form
  const [openMaterialForm, setOpenMaterialForm] = useState<boolean>(false);
  const [selectedMaterialIndex, setSelectedMaterialIndex] =
    useState<number>(-1);
  const [materialFormData, setMaterialFormData] = useState<POFormMaterial>({
    RawMaterialId: "",
    Quantity: 1,
    Price: 0,
    BaseUnit: "",
  });

  // State for notifications
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info" | "warning",
  });

  // State for detail view
  const [openDetail, setOpenDetail] = useState<boolean>(false);
  const [selectedTab, setSelectedTab] = useState<number>(0);

  // Functions to fetch data
  const fetchPurchaseOrders = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await callApi(
        apiEndpoints.NulookErpPurchaseOrderManagement,
        "POST",
        {
          operation: "get_all",
        },
      );

      if (response.error) {
        setError(response.error.message);
      } else if (response.data) {
        setPurchaseOrders((response.data as any).PurchaseOrders || []);
      }
    } catch (err) {
      setError("Failed to fetch purchase orders");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchVendors = async () => {
    try {
      const vendorRole = 3; // Vendor role ID
      const vendors = await getCompanyUsers(vendorRole);
      if (Array.isArray(vendors)) {
        setVendors(vendors);
      }
    } catch (err) {
      console.error("Failed to fetch vendors:", err);
      setSnackbar({
        open: true,
        message: "Failed to fetch vendors",
        severity: "error",
      });
    }
  };

  const fetchRawMaterials = async () => {
    try {
      const response = await getRawMaterials();
      if (response && response.rawMaterials) {
        setRawMaterialsData(response.rawMaterials);
      }
    } catch (err) {
      console.error("Failed to fetch raw materials:", err);
      setSnackbar({
        open: true,
        message: "Failed to fetch raw materials",
        severity: "error",
      });
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchPurchaseOrders();
    fetchVendors();
    fetchRawMaterials();
  }, []);

  // Handle create/update purchase order
  const handleSubmitPO = async () => {
    if (formData.RawMaterials.length === 0) {
      setSnackbar({
        open: true,
        message: "Please add at least one raw material",
        severity: "error",
      });
      return;
    }

    // Validate status changes - only allow changing to Cancelled
    if (
      formMode === "edit" &&
      selectedPO &&
      formData.Status !== selectedPO.Status
    ) {
      if (formData.Status !== "Cancelled") {
        setSnackbar({
          open: true,
          message: "Status can only be changed to 'Cancelled'",
          severity: "error",
        });
        return;
      }
    }

    try {
      setIsLoading(true);
      const operation = formMode === "create" ? "create" : "update";
      const requestData = {
        operation,
        ...formData,
      };

      // If editing, include the purchase order ID
      if (formMode === "edit" && selectedPO) {
        (requestData as any).PurchaseOrderId = selectedPO.PurchaseOrderId;
      }

      const response = await callApi(
        apiEndpoints.NulookErpPurchaseOrderManagement,
        "POST",
        requestData,
      );

      if (response.error) {
        throw new Error(response.error.message);
      }

      setSnackbar({
        open: true,
        message:
          formMode === "create"
            ? "Purchase order created successfully"
            : "Purchase order updated successfully",
        severity: "success",
      });

      // Refresh the purchase orders
      await fetchPurchaseOrders();
      handleCloseForm();
    } catch (err) {
      console.error("Error submitting purchase order:", err);
      setSnackbar({
        open: true,
        message: `Failed to ${formMode === "create" ? "create" : "update"} purchase order: ${err instanceof Error ? err.message : "Unknown error"}`,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle cancel purchase order
  const handleCancelPO = async () => {
    if (!selectedPO) return;

    try {
      setIsLoading(true);
      const response = await callApi(
        apiEndpoints.NulookErpPurchaseOrderManagement,
        "POST",
        {
          operation: "cancel",
          PurchaseOrderId: selectedPO.PurchaseOrderId,
          CancellationReason: cancellationReason,
        },
      );

      if (response.error) {
        throw new Error(response.error.message);
      }

      setSnackbar({
        open: true,
        message: "Purchase order cancelled successfully",
        severity: "success",
      });

      // Refresh the purchase orders
      await fetchPurchaseOrders();
      handleCloseForm();
    } catch (err) {
      console.error("Error cancelling purchase order:", err);
      setSnackbar({
        open: true,
        message: `Failed to cancel purchase order: ${err instanceof Error ? err.message : "Unknown error"}`,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Form handling functions
  const handleOpenCreateForm = () => {
    setFormMode("create");
    setFormData({
      VendorId: "",
      Comments: "",
      ExpectedDeliveryDate: Date.now() + 7 * 24 * 60 * 60 * 1000, // 7 days from now
      RawMaterials: [],
    });
    setOpenForm(true);
  };

  const handleOpenEditForm = (po: PurchaseOrder) => {
    setFormMode("edit");
    setSelectedPO(po);

    // Map raw materials to form format
    const mappedMaterials = po.RawMaterials.map((rm) => {
      // Find the complete raw material data
      const fullRmData = rawMaterialsData.find(
        (material) => material.RawMaterialId === rm.RawMaterialId,
      );

      return {
        RawMaterialId: rm.RawMaterialId,
        Quantity: (rm as any).Quantity || 0,
        Price: (rm as any).Price || 0,
        BaseUnit: fullRmData?.BaseUnit || "",
        UnitConversions: fullRmData?.UnitConversions,
      };
    });

    setFormData({
      VendorId: po.VendorId,
      Comments: po.Comments || "",
      ExpectedDeliveryDate: po.ExpectedDeliveryDate || Date.now(),
      RawMaterials: mappedMaterials,
      EntryExitId: po.EntryExitId || "",
      Status: po.Status,
    });
    setOpenForm(true);
  };

  const handleOpenCancelForm = (po: PurchaseOrder) => {
    setFormMode("cancel");
    setSelectedPO(po);
    setCancellationReason("");
    setOpenForm(true);
  };

  const handleOpenDetailView = (po: PurchaseOrder) => {
    setSelectedPO(po);
    setOpenDetail(true);
  };

  const handleCloseDetailView = () => {
    setOpenDetail(false);
    setSelectedPO(null);
    setSelectedTab(0);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setFormData({
      VendorId: "",
      Comments: "",
      ExpectedDeliveryDate: Date.now() + 7 * 24 * 60 * 60 * 1000,
      RawMaterials: [],
    });
    setSelectedPO(null);
    setCancellationReason("");
  };

  // Material form handling
  const handleOpenMaterialForm = (index = -1) => {
    if (index >= 0) {
      // Edit existing material
      setSelectedMaterialIndex(index);
      setMaterialFormData({
        ...formData.RawMaterials[index],
      });
    } else {
      // Add new material
      setSelectedMaterialIndex(-1);
      setMaterialFormData({
        RawMaterialId: "",
        Quantity: 1,
        Price: 0,
        BaseUnit: "",
      });
    }
    setOpenMaterialForm(true);
  };

  const handleCloseMaterialForm = () => {
    setOpenMaterialForm(false);
    setMaterialFormData({
      RawMaterialId: "",
      Quantity: 1,
      Price: 0,
      BaseUnit: "",
    });
    setSelectedMaterialIndex(-1);
  };

  const handleSubmitMaterial = () => {
    // Validate form
    if (!materialFormData.RawMaterialId || materialFormData.Quantity <= 0) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields",
        severity: "error",
      });
      return;
    }

    const updatedMaterials = [...formData.RawMaterials];

    if (selectedMaterialIndex >= 0) {
      // Update existing material
      updatedMaterials[selectedMaterialIndex] = { ...materialFormData };
    } else {
      // Add new material
      updatedMaterials.push({ ...materialFormData });
    }

    setFormData({
      ...formData,
      RawMaterials: updatedMaterials,
    });

    handleCloseMaterialForm();
  };

  const handleDeleteMaterial = (index: number) => {
    const updatedMaterials = [...formData.RawMaterials];
    updatedMaterials.splice(index, 1);
    setFormData({
      ...formData,
      RawMaterials: updatedMaterials,
    });
  };

  // Handle material ID selection
  const handleMaterialSelection = (rawMaterialId: string) => {
    const selectedMaterial = rawMaterialsData.find(
      (material) => material.RawMaterialId === rawMaterialId,
    );

    if (selectedMaterial) {
      setMaterialFormData({
        ...materialFormData,
        RawMaterialId: rawMaterialId,
        Price: selectedMaterial.Price || 0,
        BaseUnit: selectedMaterial.BaseUnit || "",
        UnitConversions: selectedMaterial.UnitConversions,
      });
    }
  };

  // Handle unit conversion
  const handleUnitConversion = (value: number) => {
    setMaterialFormData({
      ...materialFormData,
      Quantity: value,
    });
  };

  // Calculate total price
  const calculateTotalPrice = (): number => {
    return formData.RawMaterials.reduce(
      (sum, material) => sum + (material.Quantity || 0) * (material.Price || 0),
      0,
    );
  };

  // Format timestamp to readable date
  const formatDate = (timestamp: number): string => {
    return new Date(timestamp).toLocaleString();
  };

  // Render forms
  const renderCancelForm = () => (
    <Box>
      <DialogTitle>Cancel Purchase Order</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to cancel this purchase order?
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            PO ID: {selectedPO?.PurchaseOrderId}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Vendor: {selectedPO?.VendorName}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Total Price: ₹{(selectedPO?.TotalPrice || 0).toFixed(5)}
          </Typography>

          <TextField
            label="Cancellation Reason"
            fullWidth
            multiline
            rows={4}
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            sx={{ mt: 2 }}
            required
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseForm}>Close</Button>
        <Button
          onClick={handleCancelPO}
          color="error"
          variant="contained"
          disabled={!cancellationReason.trim()}
        >
          Cancel Order
        </Button>
      </DialogActions>
    </Box>
  );

  const renderCreateEditForm = () => (
    <Box>
      <DialogTitle>
        {formMode === "create"
          ? "Create New Purchase Order"
          : "Edit Purchase Order"}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Vendor</InputLabel>
              <Select
                value={formData.VendorId}
                onChange={(e) =>
                  setFormData({ ...formData, VendorId: e.target.value })
                }
                label="Vendor"
                disabled={formMode === "edit"}
                required
              >
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.UserId} value={vendor.UserId}>
                    {vendor.FullName} ({vendor.PhoneNumber})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              label="Expected Delivery Date"
              type="date"
              value={
                new Date(formData.ExpectedDeliveryDate)
                  .toISOString()
                  .split("T")[0]
              }
              onChange={(e) => {
                const newDate = new Date(e.target.value);
                setFormData({
                  ...formData,
                  ExpectedDeliveryDate: newDate.getTime(),
                });
              }}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Comments"
              fullWidth
              multiline
              rows={3}
              value={formData.Comments}
              onChange={(e) =>
                setFormData({ ...formData, Comments: e.target.value })
              }
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Raw Materials
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => handleOpenMaterialForm()}
            sx={{ mb: 2 }}
          >
            Add Material
          </Button>

          {formData.RawMaterials.length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Material Name</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Unit Price (₹)</TableCell>
                    <TableCell>Total Price (₹)</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.RawMaterials.map((material, index) => {
                    const materialData = rawMaterialsData.find(
                      (rm) => rm.RawMaterialId === material.RawMaterialId,
                    );
                    return (
                      <TableRow key={index}>
                        <TableCell>{materialData?.Name || "Unknown"}</TableCell>
                        <TableCell>
                          {(material as any).Quantity || 0}{" "}
                          {materialData?.BaseUnit || ""}
                        </TableCell>
                        <TableCell>{material.Price.toFixed(5)}</TableCell>
                        <TableCell>
                          {(
                            (material as any).Quantity * (material as any).Price
                          ).toFixed(5)}
                        </TableCell>
                        <TableCell align="center">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenMaterialForm(index)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteMaterial(index)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow>
                    <TableCell colSpan={3} align="right">
                      <Typography variant="subtitle1">Total Price:</Typography>
                    </TableCell>
                    <TableCell colSpan={2}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        ₹{(calculateTotalPrice() || 0).toFixed(5)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No materials added yet. Click "Add Material" to add raw materials.
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseForm}>Cancel</Button>
        <Button
          onClick={handleSubmitPO}
          color="primary"
          variant="contained"
          disabled={!formData.VendorId || formData.RawMaterials.length === 0}
        >
          {formMode === "create" ? "Create" : "Update"}
        </Button>
      </DialogActions>
    </Box>
  );

  const renderMaterialForm = () => {
    // Find the selected material data
    rawMaterialsData.find(
      (material) => material.RawMaterialId === materialFormData.RawMaterialId,
    );

    return (
      <Dialog
        open={openMaterialForm}
        onClose={handleCloseMaterialForm}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedMaterialIndex >= 0 ? "Edit Material" : "Add Material"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Raw Material</InputLabel>
                <Select
                  value={materialFormData.RawMaterialId}
                  onChange={(e: SelectChangeEvent) =>
                    handleMaterialSelection(e.target.value)
                  }
                  label="Raw Material"
                  required
                >
                  {rawMaterialsData
                    .filter((material) => material.Visibility)
                    .map((material) => (
                      <MenuItem
                        key={material.RawMaterialId}
                        value={material.RawMaterialId}
                      >
                        {material.Name} ({material.BaseUnit}) - ₹
                        {material.Price.toFixed(5)}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {materialFormData.RawMaterialId && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Base Unit: {materialFormData.BaseUnit}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Base Price: ₹{materialFormData.Price.toFixed(5)} per{" "}
                    {materialFormData.BaseUnit}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Quantity
                  </Typography>
                  <UnitConverter
                    mode="convert"
                    baseUnit={materialFormData.BaseUnit as UnitType}
                    baseValue={materialFormData.Quantity}
                    unitConversions={materialFormData.UnitConversions || []}
                    onChange={handleUnitConversion}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Unit Price (₹)"
                    fullWidth
                    type="number"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">₹</InputAdornment>
                      ),
                    }}
                    value={materialFormData.Price}
                    onChange={(e) =>
                      setMaterialFormData({
                        ...materialFormData,
                        Price: parseFloat(e.target.value) || 0,
                      })
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Total Price:
                    </Typography>
                    <Typography variant="h6" color="primary">
                      ₹
                      {(
                        (materialFormData.Quantity || 0) *
                        (materialFormData.Price || 0)
                      ).toFixed(5)}
                    </Typography>
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMaterialForm}>Cancel</Button>
          <Button
            onClick={handleSubmitMaterial}
            color="primary"
            variant="contained"
            disabled={
              !materialFormData.RawMaterialId || materialFormData.Quantity <= 0
            }
          >
            {selectedMaterialIndex >= 0 ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderDetailView = () => {
    if (!selectedPO) return null;

    return (
      <Dialog
        open={openDetail}
        onClose={handleCloseDetailView}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Purchase Order Details
            <Chip
              label={selectedPO.Status}
              size="small"
              sx={{
                ml: 2,
                bgcolor: statusColors[selectedPO.Status] || "#757575",
                color: "white",
              }}
            />
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
          >
            <Tab label="General Info" />
            <Tab label="Raw Materials" />
          </Tabs>

          {selectedTab === 0 && (
            <Box sx={{ p: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">PO ID:</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedPO.PurchaseOrderId}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Total Price:</Typography>
                  <Typography variant="body1" gutterBottom>
                    ₹{(selectedPO.TotalPrice || 0).toFixed(5)}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Vendor:</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedPO.VendorName}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Vendor Phone:</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedPO.VendorPhone || "N/A"}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">
                    Expected Delivery:
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedPO.ExpectedDeliveryDate
                      ? formatDate(selectedPO.ExpectedDeliveryDate)
                      : "Not specified"}
                  </Typography>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Entry/Exit ID:</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedPO.EntryExitId || "Not assigned"}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2">Comments:</Typography>
                  <Typography variant="body1" gutterBottom>
                    {selectedPO.Comments || "No comments"}
                  </Typography>
                </Grid>

                {selectedPO.Status === "Cancelled" &&
                  selectedPO.CancellationReason && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="error">
                        Cancellation Reason:
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        {selectedPO.CancellationReason}
                      </Typography>
                    </Grid>
                  )}

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 2 }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      component={Link}
                      href={selectedPO.HtmlUrl}
                      target="_blank"
                      sx={{ mr: 2 }}
                    >
                      View Printable Format
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      component={Link}
                      href={selectedPO.HtmlUrl}
                      download={`PO_${selectedPO.PurchaseOrderId}.html`}
                    >
                      Download
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {selectedTab === 1 && (
            <Box sx={{ p: 1 }}>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Material Name</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Unit Price (₹)</TableCell>
                      <TableCell>Total Price (₹)</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedPO.RawMaterials.map((material, index) => {
                      const materialData = rawMaterialsData.find(
                        (rm) => rm.RawMaterialId === material.RawMaterialId,
                      );
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            {materialData?.Name || "Unknown"}
                          </TableCell>
                          <TableCell>
                            {(material as any).Quantity || 0}{" "}
                            {materialData?.BaseUnit || ""}
                          </TableCell>
                          <TableCell>
                            {(material as any).Price?.toFixed(5) || "0.00"}
                          </TableCell>
                          <TableCell>
                            {(
                              (material as any).Quantity *
                              (material as any).Price
                            ).toFixed(5)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell colSpan={2}></TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle1">Total:</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle1" fontWeight="bold">
                          ₹{(selectedPO.TotalPrice || 0).toFixed(5)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailView}>Close</Button>
          {selectedPO.Status === "Pending" && (
            <>
              <Button
                color="primary"
                variant="outlined"
                onClick={() => {
                  handleCloseDetailView();
                  handleOpenEditForm(selectedPO);
                }}
              >
                Edit
              </Button>
              <Button
                color="error"
                variant="outlined"
                onClick={() => {
                  handleCloseDetailView();
                  handleOpenCancelForm(selectedPO);
                }}
              >
                Cancel Order
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  // Main render
  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5">Purchase Order Management</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchPurchaseOrders}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateForm}
          >
            Create Purchase Order
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>PO ID</TableCell>
                <TableCell>Vendor</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Total Price (₹)</TableCell>

                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchaseOrders.length > 0 ? (
                purchaseOrders.map((po) => (
                  <TableRow key={po.PurchaseOrderId}>
                    <TableCell>{po.PurchaseOrderId}</TableCell>
                    <TableCell>{po.VendorName}</TableCell>
                    <TableCell>
                      <Chip
                        label={po.Status}
                        size="small"
                        sx={{
                          bgcolor: statusColors[po.Status] || "#757575",
                          color: "white",
                        }}
                      />
                    </TableCell>
                    <TableCell>{po.TotalPrice?.toFixed(5) || "0.00"}</TableCell>

                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDetailView(po)}
                        title="View Details"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      {po.Status === "Pending" && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenEditForm(po)}
                            title="Edit"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenCancelForm(po)}
                            title="Cancel"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No purchase orders found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Form Dialog */}
      <Dialog open={openForm} onClose={handleCloseForm} maxWidth="md" fullWidth>
        {formMode === "cancel" ? renderCancelForm() : renderCreateEditForm()}
      </Dialog>

      {/* Material Dialog */}
      {renderMaterialForm()}

      {/* Detail View Dialog */}
      {renderDetailView()}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PurchaseOrderManagement;
